import { RwdSvgIcon } from '@/components/common/rwdSvgIcon/RwdSvgIcon';
import { AudioPlayContext } from '@/contexts/AudioPlayContext';
import { CdnContext } from '@/contexts/CdnContext';
import { ScreenContext } from '@/contexts/ScreenContext';
import { WebSocketContext } from '@/contexts/WebSocketContext';
import { useClientIp } from '@/hooks/useClientIp';
import { useUserAgent } from '@/hooks/useUserAgent';
import { Rating, Reason } from '@/models/Rating';
import { getGameState } from '@/modules/games/selector';
import { getLandingState, getMainUserState } from '@/modules/main/selector';
import { popupSliceActions } from '@/modules/popup/slice';
import { TickButton } from '@/modules/popup/svg';
import { getVHByRem } from '@/utils/commonFunc';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DialogContext, SettingDialogProps } from '../..';
import { FeedbackRating } from './FeedbackRating';
import './index.scss';

// const feedbackBlockKey = 'feedback_block';
// const feedbackSendsKey = 'feedback_sends';
declare global {
    interface Window {
        grecaptcha: ReCaptchaInstance;
    }
}
interface ReCaptchaInstance {
    ready: (callback: () => void) => void;
    execute: (sitekey: string, options: { action: string }) => Promise<string>;
}

type RatingState = {
    Reason: number;
    RateString: string;
    Rate: number;
};
type FeedbackFormState = {
    Token: string;
    Username: string;
    PlayerIP: string;
    Opinion: string;
    Displayname: string;
    WalletType: string;
    Rating: string; //Array<RatingState>;
    UI: string;
    Browser: string;
    UserAgent: string;
    Resolution: string;
    Orientation: string;
    LobbyServerProxy: string;
    LastVideoStream: string;
};
type APIResponse = {
    code: number;
    errMsg: string;
};
enum ResponseCode {
    Success = 200,
    Failvalidation,
    Limit,
    FaileReCaptcha,
    GroupErr,
}
export const FeedbackForm = (props: SettingDialogProps) => {
    const { setIsOpen } = props;
    const { feedbackApi } = useContext(CdnContext);
    const { playButtonAudio } = useContext(AudioPlayContext);
    const { setExtraCloseHandler } = useContext(DialogContext);
    // const { feedbackData, setFeedbackData } = useContext(GlobalDataContext);
    const { isLandscape } = useContext(ScreenContext);
    const [recaptchaToken, setRecaptchaToken] = useState<string | undefined>(
        undefined
    );
    const [overallRating, setOverallRating] = useState<Rating>(-1);
    const [liveVideoRating, setLiveVideoRating] = useState<Rating>(-1);
    const [liveSoundRating, setLiveSoundRating] = useState<Rating>(-1);
    const [loadingSpeedRating, setLoadingSpeedRating] = useState<Rating>(-1);
    const [dealerRating, setDealerRating] = useState<Rating>(-1);
    const [opinion, setOpinion] = useState('');
    const { t } = useTranslation();
    const { ua, browser } = useUserAgent();
    const { ip } = useClientIp();
    const { connectedUrl } = useContext(WebSocketContext);
    const { Username, IsCommonWallet } = useSelector(getMainUserState);
    const { username: LandingUsername } = useSelector(getLandingState);
    const { lastVideo, videoLibraryVersion } = useSelector(getGameState);
    const dispatch = useDispatch();
    const getRating = (reason: Reason, rating: Rating) =>
        ({
            Reason: reason,
            RateString: Rating[rating].toString(),
            Rate: rating,
        }) as RatingState;
    const getRatings = () => {
        const ratings = new Array<RatingState>();
        if (overallRating > -1)
            ratings.push(getRating(Reason.OverallExperience, overallRating));
        if (liveVideoRating > -1)
            ratings.push(getRating(Reason.QualityOfLiveVideo, liveVideoRating));
        if (liveSoundRating > -1)
            ratings.push(getRating(Reason.QualityOfLiveSound, liveSoundRating));
        if (loadingSpeedRating > -1)
            ratings.push(
                getRating(
                    Reason.LoadingSpeedOfGraphicsAndAnimations,
                    loadingSpeedRating
                )
            );
        if (dealerRating > -1)
            ratings.push(
                getRating(Reason.DealersProfessionalism, dealerRating)
            );
        return ratings;
    };
    const getToken = () => {
        //recaptcha
        const key = '6LdoZ44rAAAAAC1BbYjMybzFSRdH4MKVC5gMKah8';
        const onLoadCallback = () => {
            window.grecaptcha.ready(() => {
                window.grecaptcha
                    .execute(key, {
                        action: 'submit',
                    })
                    .then(token => {
                        setRecaptchaToken(token);
                    })
                    .catch(error =>
                        console.error(
                            `app::setting::feedback::recaptcha::gen token error: ${error}`
                        )
                    );
            });
        };

        // Load the reCAPTCHA script dynamically
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?render=' + key;
        script.async = true;
        script.defer = true;
        script.onload = onLoadCallback;
        document.body.appendChild(script);

        // Clean up the script when the component is unmounted
        return () => {
            document.body.removeChild(script);
        };
    };

    const submit = async () => {
        playButtonAudio();

        // const timestamp = feedbackData?.blockTimestamp;
        // const times = feedbackData?.sends;

        // if (timestamp !== undefined) {
        //     const now = moment().valueOf();
        //     if (now <= timestamp) {
        //         //within time range
        //         if (times && times >= 2) {
        //             dispatch(
        //                 popupSliceActions.open('popup.feedback_invalid_send')
        //             );
        //             setIsOpen(false);
        //             return;
        //         }
        //     } else {
        //         // window.localStorage.setItem(feedbackSendsKey, '0');
        //         setFeedbackData({
        //             blockTimestamp: timestamp,
        //             sends: 0,
        //         });
        //     }
        // }

        if (opinion.trim().length === 0) {
            dispatch(popupSliceActions.open('popup.feedback_checking'));
            return;
        }

        getToken();
    };

    useEffect(() => {
        setExtraCloseHandler(() => () => {});
    }, []);

    useEffect(() => {
        if (!recaptchaToken) return;

        const request = {} as FeedbackFormState;
        request.Token = recaptchaToken;
        // the opinion api is case sensitive, so dont change the case of name
        request.Username = Username;
        request.PlayerIP = ip ?? '';
        // btoa(string) does not support chinese character
        request.Opinion = btoa(unescape(encodeURIComponent(opinion)));
        request.Displayname = LandingUsername ?? '';
        request.WalletType = IsCommonWallet
            ? 'Common Wallet'
            : 'Transfer Wallet';
        request.Rating = JSON.stringify(getRatings());
        request.UI = 'Rwd UI Client';
        request.Browser = `${browser.name} ${browser.version}`;
        request.UserAgent = ua;
        request.Resolution = `${window.innerWidth}w x ${window.innerHeight}h`;
        request.Orientation = isLandscape ? 'Landscape' : 'Portrait';
        request.LobbyServerProxy = connectedUrl.current ?? '';
        request.LastVideoStream = `${lastVideo} (${videoLibraryVersion})`;
        const opinionUrl = feedbackApi;
        if (opinionUrl)
            fetch(opinionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams(request),
            })
                .then(response => {
                    response.json().then(data => {
                        console.log('app::setting::feedback', data);
                        const result = data as APIResponse;
                        if (result && result.code == ResponseCode.Success) {
                            dispatch(
                                popupSliceActions.open('popup.feedback_success')
                            );
                        } else if (
                            result &&
                            result.code == ResponseCode.Limit
                        ) {
                            dispatch(
                                popupSliceActions.open(
                                    'popup.feedback_invalid_send'
                                )
                            );
                        } else {
                            dispatch(
                                popupSliceActions.open('popup.feedback_failed')
                            );
                        }
                        // hideRecaptchaBagde();
                    });
                })
                .catch(e => {
                    console.log('app::setting::feedback::error', e);
                    dispatch(popupSliceActions.open('popup.feedback_failed'));
                });
        setIsOpen(false);

        // const next = moment().valueOf() + 60 * 60000;

        // // const lsTimes = window.localStorage.getItem(feedbackSendsKey);
        // const lsTimes = feedbackData?.sends;
        // const nextTimes = lsTimes ? lsTimes + 1 : 1;
        // // window.localStorage.setItem(feedbackSendsKey, nextTimes);
        // // window.localStorage.setItem(feedbackBlockKey, next.toString());
        // setFeedbackData({
        //     blockTimestamp: next,
        //     sends: nextTimes,
        // });
    }, [recaptchaToken]);

    return (
        <FormControl
            className={'feedback-form'}
            sx={{
                '&::-webkit-scrollbar': {
                    display: 'none', // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
                },
                '&-ms-overflow-style': {
                    display: 'none', // Hide the scrollbar for IE
                },
            }}
        >
            <Grid container className="form-container">
                <Grid item container className="form-item-list" gap={1}>
                    <FeedbackRating
                        name="feedback.overall_experience"
                        value={overallRating}
                        onChange={setOverallRating}
                        playButtonAudio={playButtonAudio}
                    />
                    <FeedbackRating
                        name="feedback.quality_of_live_video"
                        value={liveVideoRating}
                        onChange={setLiveVideoRating}
                        playButtonAudio={playButtonAudio}
                    />
                    <FeedbackRating
                        name="feedback.quality_of_live_sound"
                        value={liveSoundRating}
                        onChange={setLiveSoundRating}
                        playButtonAudio={playButtonAudio}
                    />
                    <FeedbackRating
                        name="feedback.loading_speed"
                        value={loadingSpeedRating}
                        onChange={setLoadingSpeedRating}
                        playButtonAudio={playButtonAudio}
                    />
                    <FeedbackRating
                        name="feedback.dealer_professionalism"
                        value={dealerRating}
                        onChange={setDealerRating}
                        playButtonAudio={playButtonAudio}
                    />
                    <FormControl className="opinions-text-field">
                        <FormLabel>{t('feedback.detail')}</FormLabel>
                        <TextField
                            id="feedback-text-opinions"
                            // label={t('feedback.detail')}
                            multiline
                            fullWidth
                            rows={4}
                            value={opinion}
                            onChange={e => {
                                setOpinion(e.target.value);
                            }}
                            InputProps={{ disableUnderline: true }}
                            variant="standard"
                        />
                    </FormControl>
                </Grid>
                <Grid item>
                    <FormControl className="submit-button mx-auto">
                        <RwdSvgIcon vSize={3} hSize={getVHByRem(3)}>
                            <TickButton onClick={submit} />
                        </RwdSvgIcon>
                    </FormControl>
                </Grid>
            </Grid>
        </FormControl>
    );
};
